import React from "react";
import { Text, Flex, Box, type Icon, AspectRatio } from "@chakra-ui/react";
import { Link } from "ui/link";

type UseCaseCardProps = {
  icon: { bgColor: string; color: string; icon: typeof Icon };
  title: string;
  href: string;
};

export function UseCaseCard({ icon, title, href }: UseCaseCardProps) {
  return (
    <AspectRatio className="group" ratio={1 / 1} w="100%">
      <Link
        aria-label={title}
        href={href}
        variant="unstyled"
        target="_blank"
        boxSize="100%"
        boxShadow="md"
        borderRadius={{ base: "small", sm: "big", md: "small", xl: "big" }}
        bg="brand.backgroundSecondary.light"
        display="flex"
        flexDir="column"
        alignItems="center"
        gap={{ base: "tiny", sm: "smaller" }}
        justifyContent="end"
        textAlign="center"
        pos="relative"
        px="tinier"
        py="small"
        overflow="visible !important" // Override chakra's aspect-ratio overflow behavior so the pseudo element box-shadow is visible on hover.
        transition=".2s ease"
        transitionProperty="background-color, transform"
        // Important: Do not animate box-shadow, it will have a major performance impact. Use a pseudo element instead.
        // Read more here: https://tobiasahlin.com/blog/how-to-animate-box-shadow/
        _after={{
          content: '""',
          position: "absolute",
          borderRadius: { base: "small", xl: "big" }, // Since hover only happens on md and larger screens, we only need to mirror those border radii.
          inset: 0,
          boxShadow: "hoverTranslateY.md",
          opacity: 0,
          transition: "opacity .2s ease",
        }}
        _groupHover={{
          bgColor: "rgba(243, 247, 245, 0.3)", // XX new.gray.50 with opacity adjustment
          transform: { base: "none", md: "translateY(-12px)" },
          _after: {
            opacity: { base: "none", md: 1 },
          },
        }}
        _focus={{ outline: "none" }}
        _focusVisible={{
          outline: "2px solid var(--chakra-colors-new-green-300)",
          outlineOffset: "1px",
        }}
      >
        <Flex
          align="center"
          borderRadius={{ base: "6px", sm: "10px" }}
          bg={icon.bgColor}
          boxSize={{ base: "20px", sm: "40px", md: "32px", xl: "40px" }}
          justify="center"
          flexShrink={0}
          mt="auto"
        >
          <Box as={icon.icon} boxSize={{ base: "icon.xs", sm: "icon.md" }} color={icon.color} />
        </Flex>
        <Text
          color="brand.textSecondary.light"
          textStyle="caption2"
          fontSize={{ base: "8px", sm: "10px", xl: "11px" }}
          lineHeight={{ base: "12", sm: "16" }}
          fontWeight="semibold"
          whiteSpace="pre-wrap"
          mx="tiniest"
        >
          {title}
        </Text>
      </Link>
    </AspectRatio>
  );
}
